import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Display } from './Atoms';

/**
 * ConfirmDialog — Terroir-styled zamiennik window.confirm.
 *
 * Provider renderuje pojedynczy singleton modal i rejestruje API
 * w `window.__terroirConfirm.show(opts) -> Promise<boolean>`.
 *
 * Imperative API z `redesign/lib/confirm.js`:
 *   import { confirmDialog } from '../lib/confirm';
 *   if (await confirmDialog('Usunąć zadanie?')) { ... }
 *   if (await confirmDialog('Usunąć konto?', {
 *     title: 'Czy na pewno?',
 *     confirmLabel: 'Usuń konto',
 *     tone: 'danger',
 *   })) { ... }
 */

const DEFAULT_OPTS = {
  title: 'Czy na pewno?',
  message: '',
  confirmLabel: 'Tak',
  cancelLabel: 'Anuluj',
  tone: 'sage', // 'sage' | 'danger' | 'amber'
};

export default function ConfirmDialogProvider() {
  const [opts, setOpts] = useState(null);
  const resolverRef = useRef(null);

  const close = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(Boolean(result));
      resolverRef.current = null;
    }
    setOpts(null);
  }, []);

  useEffect(() => {
    window.__terroirConfirm = {
      show: (options) => new Promise((resolve) => {
        resolverRef.current = resolve;
        setOpts({
          ...DEFAULT_OPTS,
          ...(typeof options === 'string' ? { message: options } : options),
        });
      }),
    };
    return () => { delete window.__terroirConfirm; };
  }, []);

  // Esc / Enter
  useEffect(() => {
    if (!opts) return;
    const handler = (e) => {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter')  close(true);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [opts, close]);

  if (!opts) return null;

  const accent = opts.tone === 'danger' ? 'var(--t-rust)'
               : opts.tone === 'amber'  ? 'var(--t-amber)'
               : 'var(--t-sage)';

  return (
    <div
      className="redesign-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      onClick={(e) => { if (e.target === e.currentTarget) close(false); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9800,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'grid', placeItems: 'center',
        padding: 16,
      }}
    >
      <div style={{
        width: '100%', maxWidth: 420,
        background: 'var(--t-bg-elev)',
        border: `1px solid ${accent}`,
        borderRadius: 16,
        padding: 24,
        animation: 't-toast-in 220ms cubic-bezier(.2,.8,.2,1)',
      }}>
        <div style={{
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.22em', textTransform: 'uppercase',
          color: accent, marginBottom: 8,
        }}>{opts.tone === 'danger' ? 'Strefa niebezpieczna' : opts.tone === 'amber' ? 'Uwaga' : 'Potwierdź'}</div>
        <Display id="confirm-title" size={22} style={{
          display: 'block', letterSpacing: '-.02em', marginBottom: 10,
        }}>{opts.title}</Display>
        {opts.message && (
          <div style={{
            fontSize: 14, lineHeight: 1.55,
            color: 'var(--t-text-dim)',
            marginBottom: 22,
          }}>{opts.message}</div>
        )}
        <div style={{
          display: 'flex', gap: 10, justifyContent: 'flex-end',
        }}>
          <button
            type="button"
            onClick={() => close(false)}
            autoFocus
            style={{
              padding: '10px 18px', borderRadius: 10,
              background: 'transparent',
              border: '1px solid var(--t-line-2)',
              color: 'var(--t-text-dim)',
              fontFamily: 'var(--t-sans)', fontSize: 14,
              cursor: 'pointer',
            }}
          >{opts.cancelLabel}</button>
          <button
            type="button"
            onClick={() => close(true)}
            style={{
              padding: '10px 20px', borderRadius: 10,
              background: accent,
              border: 'none',
              color: 'var(--t-bg)',
              fontFamily: 'var(--t-sans)', fontSize: 14, fontWeight: 500,
              cursor: 'pointer', letterSpacing: '-.01em',
            }}
          >{opts.confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
